/**
 * One member's cosmetics, for the admin profile panel.
 *
 * Reads the PUBLIC `/api/v1/cosmetics/users` endpoint rather than widening
 * `/api/admin/analytics`, for three reasons:
 *
 *   1. It is already live, already edge-cached for 60s, and already the shape a
 *      consumer wants (`equipped` per slot, `owned`, `member_number`, plus an
 *      `expand=1` side map carrying each cosmetic's name and art URL).
 *   2. The admin analytics call already reads nine tables for every member. This
 *      is looked at one row at a time, so loading every member's cosmetics into
 *      that response would be a table scan and a catalog join to render something
 *      almost none of which is ever displayed.
 *   3. It leaks nothing new. The endpoint is public by design: these are the
 *      badges people wear in chat.
 */

/** A cosmetic definition as `?expand=1` returns it. */
export interface MemberCosmetic {
  slug: string;
  name: string;
  description: string | null;
  kind: string;
  slot: string;
  /** Null for a cosmetic with no art of its own: an atmosphere built purely
   *  from CSS gradients (`aurora`, `old-glory`, `cs2-major-cologne`) carries
   *  its look in a render block instead. Was typed `string` while the side map
   *  only ever described EQUIPPED slots, which made an art-less entry rare
   *  enough to go unnoticed rather than impossible. */
  image_url: string | null;
  animated: boolean;
}

export interface MemberCosmetics {
  /** False when the id has no `user_numbers` row, i.e. not a StreamNook member. */
  member: boolean;
  member_number: number | null;
  /** Slot -> slug, for every slot they have filled. */
  equipped: Record<string, string>;
  /** Every slug they own, equipped or not. */
  owned: string[];
  /** Definitions for the slugs above, keyed by slug. */
  bySlug: Record<string, MemberCosmetic>;
}

interface UsersResponse {
  users: Record<
    string,
    {
      member: boolean;
      member_number: number | null;
      equipped: Record<string, string>;
      owned: string[];
    }
  >;
  cosmetics?: Record<string, MemberCosmetic>;
}

/**
 * Fetch one member's cosmetics.
 *
 * Throws only on a transport or shape failure, so the caller can distinguish
 * "not a member" (a normal answer) from "could not ask". An unknown id comes back
 * as `member: false` rather than a 404, which is the endpoint's own contract.
 */
export async function fetchMemberCosmetics(
  twitchUserId: string,
  signal?: AbortSignal,
): Promise<MemberCosmetics> {
  const res = await fetch(
    `/api/v1/cosmetics/users?ids=${encodeURIComponent(twitchUserId)}&expand=1`,
    { signal },
  );
  if (!res.ok) throw new Error(`cosmetics_failed:${res.status}`);
  const body = (await res.json()) as UsersResponse;
  const row = body.users?.[twitchUserId];
  if (!row) throw new Error('cosmetics_failed:missing_row');
  return {
    member: row.member,
    member_number: row.member_number,
    equipped: row.equipped ?? {},
    owned: row.owned ?? [],
    bySlug: body.cosmetics ?? {},
  };
}

/**
 * Owned cosmetics as a display list: equipped first, then the rest.
 *
 * A slug with no definition in the side map is still dropped rather than
 * rendered as a gap, but that is now a genuine can't-happen rather than the
 * everyday case it used to be. Until 2026-09-20 `referencedCosmetics` walked
 * `equipped` alone, so the side map never described the `owned` array at all
 * and this panel silently showed 2 of one member's 16 cosmetics. The comment
 * here blamed cosmetics "with no art, which /catalog already refuses to
 * publish", which was wrong twice over: the side map is not built from
 * /catalog, and a cosmetic with no art is exactly what an art-less atmosphere
 * IS. Those render from `image_url: null` now instead of vanishing.
 */
export function ownedForDisplay(
  c: MemberCosmetics,
): Array<MemberCosmetic & { equipped: boolean }> {
  const equippedSlugs = new Set(Object.values(c.equipped));
  return c.owned
    .map((slug) => {
      const def = c.bySlug[slug];
      return def ? { ...def, equipped: equippedSlugs.has(slug) } : null;
    })
    .filter((x): x is MemberCosmetic & { equipped: boolean } => x !== null)
    .sort((a, b) => {
      if (a.equipped !== b.equipped) return a.equipped ? -1 : 1;
      if (a.kind !== b.kind) return a.kind.localeCompare(b.kind);
      return a.name.localeCompare(b.name);
    });
}
